export const smallestUnitToBase = (amount: number, currency: "EUR"): number => {
  if (!Number.isInteger(amount)) {
    throw new Error(`expected integer, got: ${amount}`);
  }

  if (!Number.isSafeInteger(amount)) {
    throw new Error(`expected safe integer, got: ${amount}`);
  }

  if (currency === "EUR") {
    return amount / 100;
  }

  throw new Error(`${currency} is not supported`);
};

export const baseUnitToSmallest = (amount: number, currency: "EUR"): number => {
  if (currency === "EUR") {
    const result = amount * 100;

    if (!Number.isInteger(result)) {
      throw new Error(`conversion resulted in non-integer: ${result}`);
    }

    if (!Number.isSafeInteger(result)) {
      throw new Error(`conversion resulted in unsafe integer: ${result}`);
    }

    return result;
  }

  throw new Error(`${currency} is not supported`);
};

export const currencyToLocaleString = (
  amount: number | null,
  currency: "EUR",
  locale?: string | string[],
  options?: {
    display: "code" | "symbol" | "narrowSymbol" | "name";
    sign: "standard" | "accounting";
  },
): string => {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: options?.display,
    currencySign: options?.sign,
  });

  if (amount === null) {
    if (currency === "EUR") {
      return formatter.format(0).replaceAll("0", "-");
    }

    throw new Error(`${currency} is not supported`);
  }

  if (!Number.isSafeInteger(amount)) {
    throw new Error(`expected a safe integer, got: ${amount}`);
  }

  if (currency === "EUR") {
    return formatter.format(amount / 100);
  }

  throw new Error(`${currency} is not supported`);
};

export const computeVat = (amount: number, vat: number, currency: "EUR") => {
  if (!Number.isSafeInteger(amount)) {
    throw new Error(`amount must be a safe integer, got: ${amount}`);
  }

  if (!Number.isFinite(vat)) {
    throw new Error(`vat percentage must be a finite number, got ${vat}`);
  }

  if (vat < 0 || vat > 1) {
    throw new Error(
      `vat percentage must be between 0 and 1 inclusive, got ${vat}`,
    );
  }

  if (currency === "EUR") {
    return Math.round(amount * vat);
  }

  throw new Error(`${currency} is not supported`);
};

export const applyVat = (amount: number, vat: number, currency: "EUR") => {
  if (!Number.isSafeInteger(amount)) {
    throw new Error(`amount must be a safe integer, got: ${amount}`);
  }

  if (!Number.isFinite(vat)) {
    throw new Error(`vat percentage must be a finite number, got ${vat}`);
  }

  if (vat < 0 || vat > 1) {
    throw new Error(
      `vat percentage must be between 0 and 1 inclusive, got ${vat}`,
    );
  }

  if (currency === "EUR") {
    return amount + Math.round(amount * vat);
  }

  throw new Error(`${currency} is not supported`);
};

export const removeVat = (amount: number, vat: number, currency: "EUR") => {
  if (!Number.isSafeInteger(amount)) {
    throw new Error(`amount must be a safe integer, got: ${amount}`);
  }

  if (!Number.isFinite(vat)) {
    throw new Error(`vat percentage must be a finite number, got ${vat}`);
  }

  if (vat < 0 || vat > 1) {
    throw new Error(
      `vat percentage must be between 0 and 1 inclusive, got ${vat}`,
    );
  }

  if (currency === "EUR") {
    let approximateValueBeforeVat = Math.round(amount / (1 + vat));

    if (applyVat(approximateValueBeforeVat, vat, currency) === amount) {
      return approximateValueBeforeVat;
    }

    approximateValueBeforeVat += 1;

    if (applyVat(approximateValueBeforeVat, vat, currency) === amount) {
      return approximateValueBeforeVat;
    }

    approximateValueBeforeVat -= 2;

    if (applyVat(approximateValueBeforeVat, vat, currency) === amount) {
      return approximateValueBeforeVat;
    }

    throw new Error("unable to compute amount before vat");
  }

  throw new Error(`${currency} is not supported`);
};
